// CÓMO SE CUENTA LA COMISIÓN DE CADA TIPO DE PÓLIZA.
//
// Lissette, 2026-10-11, en Comisiones por Póliza: *"el formato de salud es
// solo para salud; las demás pólizas, excepto vida y gastos finales, solo deja
// prima, el porcentaje que nos pagan por esa prima y el total — esas pólizas
// las pagan por porcentaje de prima"*. Y para vida: adelanto de los primeros
// meses (normalmente 9, que se pueda cambiar) y el resto mes a mes, con el
// total = prima anual × % de la aseguradora. Confirmado con ella con este
// ejemplo antes de escribir nada:
//
//     prima $100/mes → $1,200 al año
//     la aseguradora paga 80%        → $960 el primer año
//     adelanto de 9 meses            → $720 al principio
//     meses 10, 11 y 12              → $80 cada uno
//     con split de 50%               → nos quedan $480
//
// Tres formatos:
//
//   salud  — $ por persona × personas × % split (comisionPorPersona.js).
//   vida   — Vida y Gastos Finales: prima × 12 × % de la prima × % split,
//            con adelanto de N meses.
//   prima  — todo lo demás: prima × % de la prima × % split, UNA SOLA VEZ.
//            Sin meses (Lissette, el mismo día: "la única que pagan mensual
//            es la de salud… las demás, el total es la prima por el porcentaje
//            que nos pagarán, pero solo es una vez, no mensual"). El split
//            también aquí ("si es de agencia, si es split").
//
// El "% de la prima" (lo que paga la aseguradora sobre la prima) es un campo
// NUEVO, `porcentajeDePrima`, y no el `commissionPercent` de siempre: ese
// guarda el split en salud, y leerlo como % de la prima cambiaría solos los
// totales de pólizas que ya están cargadas. Por lo mismo, mientras una fila no
// tenga el % de la prima escrito, su total se sigue contando como antes.
//
// Vive suelto, sin React, por el mismo motivo que comisionPorPersona.js: es
// aritmética de dinero y se prueba por lo que devuelve.
import { lineaDelRamo } from './lineasDeBloqueo.js';
import { numero } from './comisionPorPersona.js';

/** Meses de adelanto si la póliza no dice otra cosa. */
export const MESES_DE_ADELANTO = 9;

/**
 * 'salud' | 'vida' | 'prima'. Sale de la MISMA tabla de ramos que el bloqueo
 * del calendario (lineasDeBloqueo.js): Salud y Short Term son salud; Vida y
 * Gastos Finales son vida; todo lo demás —también los ramos propios de la
 * agencia— se paga por porcentaje de la prima.
 */
export function formatoDeComision(tipo) {
  const linea = lineaDelRamo(tipo);
  if (linea === 'salud') return 'salud';
  if (linea === 'vida') return 'vida';
  return 'prima';
}

const redondear = (n) => Math.round(n * 100) / 100;

/** Meses de adelanto válidos: entre 1 y 12; si no, los de siempre. */
export function mesesDeAdelanto(v) {
  const n = Math.trunc(numero(v));
  return n >= 1 && n <= 12 ? n : MESES_DE_ADELANTO;
}

/**
 * Vida y Gastos Finales: el primer año.
 *
 * El split (lo que nos queda) en blanco es 100%, igual que en salud.
 * Devuelve null si falta la prima o el % de la prima: sin esos dos no hay
 * cuenta, y un $0 calculado no se distingue de un $0 de verdad.
 */
export function comisionDeVida({ primaMensual, porcentajeDePrima, split, meses }) {
  const prima = numero(primaMensual);
  const pct = numero(porcentajeDePrima);
  if (!(prima > 0) || !(pct > 0)) return null;
  const parte = numero(split) > 0 ? numero(split) / 100 : 1;
  const anual = prima * 12;
  const total = redondear(anual * (pct / 100) * parte);
  const adelantoMeses = mesesDeAdelanto(meses);
  const porMes = redondear(total / 12);
  const adelanto = redondear(total * adelantoMeses / 12);
  return {
    primaAnual: redondear(anual),
    total,
    adelantoMeses,
    adelanto,
    porMes,
    mesesRestantes: 12 - adelantoMeses,
    // Lo que falta después del adelanto, para que adelanto + resto = total
    // exacto aunque el redondeo de los meses no cuadre al centavo.
    resto: redondear(total - adelanto),
  };
}

/**
 * Las demás pólizas: prima × % de la prima × % split, una sola vez (no por
 * mes), o null. El split en blanco es 100%, como en salud y vida.
 */
export function comisionPorPrima({ prima, porcentajeDePrima, split }) {
  const p = numero(prima);
  const pct = numero(porcentajeDePrima);
  if (!(p > 0) || !(pct > 0)) return null;
  const parte = numero(split) > 0 ? numero(split) / 100 : 1;
  return redondear(p * (pct / 100) * parte);
}
